/**
 * Flux design tokens for Kira Admin (Agents product).
 * Hex values mirror frontend/src/tokens/flux.css for MUI createTheme.
 * Prefer CSS variables (FLUX_CSS) in sx/styleOverrides where possible.
 */

export const FLUX_CSS = {
  agentsAccent: "var(--flux-agents-accent)",
  layerAgents: "var(--flux-layer-agents)",
  layerCore: "var(--flux-layer-core)",
  primary300: "var(--flux-primary-300)",
  primary400: "var(--flux-primary-400)",
  primary800: "var(--flux-primary-800)",
  pink400: "var(--flux-pink-400)",
  purple400: "var(--flux-purple-400)",
  blue400: "var(--flux-blue-400)",
  success: "var(--flux-success)",
  error: "var(--flux-error)",
  warning: "var(--flux-warning)",
  info: "var(--flux-info)",
  background: "var(--flux-background)",
  surface: "var(--flux-surface)",
  surfaceAlt: "var(--flux-surface-alt)",
  text: "var(--flux-black)",
  textMuted: "var(--flux-grey-500)",
  heading: "var(--flux-heading)",
  border: "var(--flux-grey-100)",
  borderSubtle: "var(--flux-grey-50)",
  link: "var(--flux-link)",
  linkHover: "var(--flux-link-hover)",
  codeBg: "var(--flux-code-bg)",
  codeText: "var(--flux-code-text)",
  shadowSm: "var(--flux-shadow-sm)",
  shadowMd: "var(--flux-shadow-md)",
  shadowLg: "var(--flux-shadow-lg)",
  shadowXl: "var(--flux-shadow-xl)",
} as const;

/** Concrete values required by MUI createTheme at initialization. */
export const FLUX_HEX = {
  agentsAccent: "#8B5CF6",
  primary400: "#056F82",
  primary300: "#69A9B4",
  primary800: "#032E36",
  pink400: "#DE37A4",
  purple400: "#6F59FF",
  blue400: "#4285FF",
  success: "#10B981",
  error: "#C1121F",
  warning: "#F59E0B",
  info: "#056F82",
  neutral: "#8AA0A5",
  neutralDark: "#64748B",
  background: "#0A1416",
  paper: "#12282D",
  textPrimary: "#E5EAEB",
  textSecondary: "#8AA0A5",
  divider: "#1E3A40",
  white: "#FFFFFF",
} as const;

/** @deprecated Use FLUX_CSS or FLUX_HEX */
export const FLUX = {
  primary: FLUX_HEX.agentsAccent,
  secondary: FLUX_HEX.primary400,
  background: FLUX_CSS.background,
  surface: FLUX_CSS.surface,
  surfaceAlt: FLUX_CSS.surfaceAlt,
  text: FLUX_CSS.text,
  textMuted: FLUX_CSS.textMuted,
  heading: FLUX_CSS.heading,
  border: FLUX_CSS.border,
  borderSubtle: FLUX_CSS.borderSubtle,
  error: FLUX_HEX.error,
  warning: FLUX_HEX.warning,
  success: FLUX_HEX.success,
  info: FLUX_HEX.info,
  link: FLUX_CSS.link,
  linkHover: FLUX_CSS.linkHover,
  codeBg: FLUX_CSS.codeBg,
  codeText: FLUX_CSS.codeText,
};

export const STATUS_COLORS = {
  success: FLUX_HEX.success,
  warning: FLUX_HEX.warning,
  error: FLUX_HEX.error,
  info: FLUX_HEX.info,
  neutral: FLUX_HEX.neutral,
  agents: FLUX_HEX.agentsAccent,
  teal: FLUX_HEX.primary300,
  /** @deprecated Use agents */
  purple: FLUX_HEX.agentsAccent,
} as const;

export const SCORE_COLORS = {
  high: FLUX_HEX.success,
  medium: FLUX_HEX.warning,
  low: FLUX_HEX.error,
  critical: FLUX_HEX.error,
} as const;

export type ScoreLevel = keyof typeof SCORE_COLORS;

export const QUALITY_BANDS: { min: number; max: number; label: string; color: string }[] = [
  { min: 80, max: 100, label: "Good", color: SCORE_COLORS.high },
  { min: 60, max: 79, label: "Acceptable", color: FLUX_HEX.primary300 },
  { min: 40, max: 59, label: "Needs Improvement", color: SCORE_COLORS.medium },
  { min: 0, max: 39, label: "Significant Issues", color: SCORE_COLORS.low },
];

export function getQualityBand(score: number) {
  return QUALITY_BANDS.find((b) => score >= b.min && score <= b.max) ?? QUALITY_BANDS[3];
}

/** Development ticket pipeline statuses mapped to Flux palette tokens only. */
export const TICKET_STATUS_COLORS: Record<string, string> = {
  queued: FLUX_HEX.neutralDark,
  assessing: FLUX_HEX.agentsAccent,
  awaiting_info: FLUX_HEX.pink400,
  awaiting_approval: FLUX_HEX.purple400,
  awaiting_design_approval: FLUX_HEX.purple400,
  investigating: FLUX_HEX.primary300,
  authoring: FLUX_HEX.warning,
  reviewing: FLUX_HEX.blue400,
  mr_created: FLUX_HEX.success,
  iterating: FLUX_HEX.primary400,
  ready_for_qa: FLUX_HEX.success,
  completed: FLUX_HEX.success,
  declined: FLUX_HEX.neutral,
  failed: FLUX_HEX.error,
  escalated: FLUX_HEX.error,
};

export const CATEGORY_COLORS: Record<string, string> = {
  new_feature: FLUX_HEX.blue400,
  feature_tweak: FLUX_HEX.agentsAccent,
  bug_fix: FLUX_HEX.warning,
};

export function qualityScoreColor(score: number): string {
  if (score >= 70) return SCORE_COLORS.high;
  if (score >= 40) return SCORE_COLORS.medium;
  return SCORE_COLORS.low;
}

export const CONFIDENCE_COLORS: Record<string, string> = {
  high: SCORE_COLORS.high,
  medium: SCORE_COLORS.medium,
  low: SCORE_COLORS.low,
};

export const DECISION_COLORS: Record<string, string> = {
  accepted: SCORE_COLORS.high,
  declined: SCORE_COLORS.low,
  pending_approval: SCORE_COLORS.medium,
};

export const RESOLUTION_COLORS: Record<string, string> = {
  done: SCORE_COLORS.high,
  rejected: SCORE_COLORS.low,
};

export function ticketStatusColor(status: string): string {
  return TICKET_STATUS_COLORS[status] ?? FLUX_HEX.neutral;
}

/** MUI sx class for cards that should show hover elevation (clickable metrics, etc.). */
export const INTERACTIVE_CARD_CLASS = "flux-card-interactive";
